// src/components/ViewDeploymentForm.js
import React, { useEffect, useState, useMemo } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";            // ✅ IMPORTANT: needed for doc.autoTable
import "./AddDeploymentForm.css";      // reuse modal + table look
import "./ViewDeploymentForm.css";     // header/info layout
import DeploymentFilesModal from "./DeploymentFilesModal";

export default function ViewDeploymentForm({ deploymentId, onClose }) {
  const [deployment, setDeployment] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [files, setFiles] = useState({
  ackPdf: null,
  photos: [],
  });
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState('');
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [verifyingAll, setVerifyingAll] = useState({}); // { DEPLOY: bool, COLLECT: bool, TRANSFER: bool }
  const [sendingApproval, setSendingApproval] = useState(false);

  // Re-fetch header + items without showing the full "Loading…" state
  const refreshDeployment = async () => {
    if (!deploymentId) return;
    const res = await fetch(`/api/deployments/${deploymentId}`);
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setDeployment(data.deployment);
      setItems(Array.isArray(data.items) ? data.items : []);
    }
  };

  // Files come oldest first. Older deployments may still have several ACK PDFs
  // from before re-uploads replaced them, so show the newest one.
  const applyFiles = (list) => {
    const all = Array.isArray(list) ? list : [];
    const acks = all.filter((f) => f.file_type === 'ACK_PDF');
    setFiles({
      ackPdf: acks.length ? acks[acks.length - 1] : null,
      photos: all.filter((f) => f.file_type === 'PHOTO'),
    });
  };

  // Download links are signed and expire after 1 hour, so this also runs on a timer
  const loadFiles = async ({ silent = false } = {}) => {
    if (!deploymentId) return;
    if (!silent) setFileLoading(true);
    setFileError('');
    try {
      const res = await fetch(`/api/deployments/${deploymentId}/files`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          res.status === 401 || res.status === 403
            ? 'Your session has expired. Please log in again to see attachments.'
            : data.error || 'Failed to load attachments.'
        );
      }
      applyFiles(data.files);
    } catch (err) {
      console.error('Error loading deployment files', err);
      setFileError(err.message || 'Failed to load attachments.');
    } finally {
      if (!silent) setFileLoading(false);
    }
  };

  useEffect(() => {
    if (!deploymentId) return undefined;
    loadFiles();
    const timer = setInterval(() => loadFiles({ silent: true }), 45 * 60 * 1000);
    return () => clearInterval(timer);
  }, [deploymentId]);

  const handleDeleteFile = async (file, label) => {
    if (!window.confirm(`Delete ${label}?\n\n${file.original_name}`)) return;

    setDeletingFileId(file.file_id);
    setFileError('');
    try {
      const res = await fetch(`/api/deployment-files/${file.file_id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          res.status === 401 || res.status === 403
            ? 'Your session has expired. Please log in again.'
            : data.error || 'Failed to delete file.'
        );
      }
      applyFiles(data.files);
    } catch (err) {
      console.error('Delete file error', err);
      setFileError(err.message);
    } finally {
      setDeletingFileId(null);
    }
  };

   const handleVerifyAll = async (actionType) => {
  if (!deploymentId || !deployment) return;
  if (awaitingApproval) {
    alert('This deployment has FOC devices and cannot be completed until Samsung Finance approves it.');
    return;
  }

  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('user_id'); // adjust key if your app stores it differently

  if (!token) {
    alert('You appear to be logged out. Please log in again.');
    return;
  }

  const confirmed = window.confirm(
    `Mark all pending ${actionType.toLowerCase()} items as verified? This cannot be undone.`
  );
  if (!confirmed) return;

  setVerifyingAll(prev => ({ ...prev, [actionType]: true }));
  try {
    const res = await fetch(`/api/deployments/${deploymentId}/verify-all`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ action_type: actionType, user_id: userId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // Server-side approval gate (e.g. approval was reset by an edit) — resync the UI
      if (data.code === 'APPROVAL_REQUIRED') refreshDeployment().catch(() => {});
      throw new Error(data.error || data.message || 'Failed to verify items.');
    }

    alert(data.message || `${data.updated} item(s) verified.`);

    // Refresh the deployment so item statuses update in the table
    await refreshDeployment();
  } catch (err) {
    alert(`Error: ${err.message}`);
  } finally {
    setVerifyingAll(prev => ({ ...prev, [actionType]: false }));
  }
};

  // ---- Load deployment details ----
  useEffect(() => {
    if (!deploymentId) return;

    setLoading(true);
    setError("");

    fetch(`/api/deployments/${deploymentId}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          throw new Error(data.error || "Failed to load deployment.");
        }
        return data;
      })
      .then(({ deployment, items }) => {
        setDeployment(deployment);
        setItems(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        console.error("View deployment error:", err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [deploymentId]);

  // ---- Derived lists ----
  const deployItems = useMemo(
    () => items.filter((it) => it.action_type === "DEPLOY"),
    [items]
  );
  const collectItems = useMemo(
    () => items.filter((it) => it.action_type === "COLLECT"),
    [items]
  );
  const transferItems = useMemo(
    () => items.filter((it) => it.action_type === "TRANSFER"),
    [items]
  );

  // ---- Samsung Finance approval (required when the deployment has FOC devices) ----
  const focItems = useMemo(
    () => items.filter((it) => it.inventory_type === "FOC"),
    [items]
  );
  const requiresApproval = deployment?.requires_approval ?? focItems.length > 0;
  const approvalStatus = deployment?.approval_status || null; // null | 'PENDING' | 'APPROVED'
  const isApproved = approvalStatus === "APPROVED";
  const awaitingApproval = requiresApproval && !isApproved;
  const canSendForApproval =
    requiresApproval && !isApproved && deployment?.status === "IN_PROGRESS";
  const showApprovalPanel =
    requiresApproval && (deployment?.status === "IN_PROGRESS" || !!approvalStatus);

  // While a request is out, poll so the page unlocks as soon as Samsung Finance approves
  useEffect(() => {
    if (!deploymentId || deployment?.status !== "IN_PROGRESS" || approvalStatus !== "PENDING") return;
    const timer = setInterval(() => {
      refreshDeployment().catch(() => {});
    }, 10000);
    return () => clearInterval(timer);
  }, [deploymentId, deployment?.status, approvalStatus]);

  const handleSendForApproval = async () => {
    if (!deployment) return;

    const token = localStorage.getItem("token");
    if (!token) {
      alert("You appear to be logged out. Please log in again.");
      return;
    }

    const isResend = approvalStatus === "PENDING";
    const confirmed = window.confirm(
      isResend
        ? "Resend the approval email to Samsung Finance? Links in the earlier email will stop working."
        : `Email the approval form for ${focItems.length} FOC device${focItems.length === 1 ? "" : "s"} to Samsung Finance?`
    );
    if (!confirmed) return;

    setSendingApproval(true);
    try {
      const res = await fetch(`/api/deployments/${deploymentId}/send-approval`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || "Failed to send for approval.");

      let msg = `Approval request sent to: ${(data.sent_to || []).join(", ")}`;
      if (data.failed?.length) msg += `\n\nCould not send to: ${data.failed.join(", ")}`;
      alert(msg);

      await refreshDeployment();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setSendingApproval(false);
    }
  };

  const formatStatus = (status) => {
    if (!status) return "-";
    switch (status) {
      case "IN_PROGRESS":
        return "In Progress";
      case "COMPLETED":
        return "Completed";
      case "CANCELLED":
        return "Cancelled";
      default:
        return status.replace(/_/g, " ");
    }
  };

  const formatItemStatus = (s) => (s ? s.replace(/_/g, " ") : "-");

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-SG", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

const handleGeneratePdf = async () => {
  if (!deployment) {
    alert("Deployment details are still loading. Please try again in a moment.");
    return;
  }

  let dmNo = deployment.device_movement_no;

  if (!dmNo) {
    try {
      const res = await fetch(
        `/api/deployments/${deployment.deployment_id}/assign-device-movement-no`,
        { method: "POST" }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to assign DO / DM number");
      dmNo = data.device_movement_no;
      setDeployment((prev) => prev ? { ...prev, device_movement_no: dmNo } : prev);
    } catch (e) {
      alert(`Unable to generate DO number: ${e.message}`);
      return;
    }
  }

  const doc = new jsPDF("p", "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 14;

  // ── Define renderPdf BEFORE referencing it in onload/onerror ──
  const renderPdf = (doc, pageW, marginX, dmNo, img) => {
    let y = 10;

    // ── 1. LOGO ──
    if (img) {
      const maxLogoW = 55;
      const naturalW = img.naturalWidth  || img.width  || 1;
      const naturalH = img.naturalHeight || img.height || 1;
      const logoW = Math.min(maxLogoW, (pageW * naturalW) / 595.28);
      const logoH = logoW * (naturalH / naturalW);
      doc.addImage(img, "PNG", marginX, y, logoW, logoH);
      y += logoH + 8;
    } else {
      y += 10;
    }

    // ── 2. TITLE ──
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("DEVICE MOVEMENT ACKNOWLEDGEMENT FORM", pageW / 2, y, { align: "center" });
    y += 12;

    // ── 3. HEADER GRID ──
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    const lLbl   = marginX;
    const lColon = marginX + 33;
    const lVal   = marginX + 36;
    const rLbl   = 105;
    const rColon = 105 + 28;
    const rVal   = 105 + 31;
    const rowH   = 6;

    doc.text("Location",                                 lLbl,   y);
    doc.text(":",                                        lColon, y);
    doc.text(deployment.deployment_location_name || "-", lVal,   y);
    y += rowH;

    const leftRows = [
      { label: "Deployment Type", value: deployment.deployment_type || "-" },
      { label: "Created At",      value: formatDateTime(deployment.created_at) },
    ];
    const rightRows = [
      { label: "DO No.",     value: dmNo || "-" },
      { label: "Created By", value: deployment.created_by_name || "-" },
      { label: "Vendor",     value: deployment.vendor_name || "-" },
    ];

    const startY = y;
    leftRows.forEach((r, i) => {
      const ry = startY + i * rowH;
      doc.text(r.label, lLbl,   ry);
      doc.text(":",     lColon, ry);
      doc.text(r.value, lVal,   ry);
    });
    rightRows.forEach((r, i) => {
      const ry = startY + i * rowH;
      doc.text(r.label, rLbl,   ry);
      doc.text(":",     rColon, ry);
      doc.text(r.value, rVal,   ry);
    });

    y = startY + Math.max(leftRows.length, rightRows.length) * rowH + 6;

    // ── 4. SECTION TABLE HELPER ──
    const drawTable = (title, rows) => {
      if (!rows.length) return;

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(title, marginX, y);
      y += 5;

      const head = [[
        "No.", "Model", "Colour", "Imei Number", "Serial Number", "Inv. Type", "Partner"
      ]];

      const body = rows.map((item, idx) => [
        idx + 1,
        item.asset1_name    || "",
        item.asset2_name    || "",
        item.imei_number    || "",
        item.serial_number  || "",
        item.inventory_type || "",
        item.partner_name   || "",
      ]);

      autoTable(doc, {
        startY: y,
        head,
        body,
        theme: "grid",
        styles:     { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: "bold" },
        margin:     { left: marginX, right: marginX },
      });

      y = doc.lastAutoTable.finalY + 8;
    };

    drawTable("Devices Deployed",       deployItems);
    drawTable("Devices Collected",      collectItems);
    drawTable("PO Devices Handed Over", transferItems);

    // ── 5. ACKNOWLEDGEMENT ──
    if (y > 240) { doc.addPage(); y = 20; }

    y += 6;
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("ACKNOWLEDGEMENT", marginX, y);
    y += 6;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(
      "I acknowledge that the devices listed above have been deployed / collected / transferred",
      marginX, y
    );
    y += 5;
    doc.text("in good working order, unless otherwise stated.", marginX, y);
    y += 12;

    // ── 6. SIGNATURE BLOCK ──
    const lineLen = 60;
    const rSig = 105;

    const sigRows = [
      { label: "Handled By:", rLabel: "Received By:" },
      { label: "Signature:",  rLabel: "Signature:" },
      { label: "Date:",       rLabel: "Date:", shortLine: true },
    ];

    sigRows.forEach((row, i) => {
      const ry = y + i * 10;
      const lLineStart = marginX + 28;
      const rLineStart = rSig + 30;
      const lLineEnd   = lLineStart + (row.shortLine ? 35 : lineLen);
      const rLineEnd   = rLineStart + (row.shortLine ? 35 : lineLen);

      doc.text(row.label,  marginX, ry);
      doc.line(lLineStart, ry + 0.5, lLineEnd, ry + 0.5);
      doc.text(row.rLabel, rSig,    ry);
      doc.line(rLineStart, ry + 0.5, rLineEnd, ry + 0.5);
    });

    // ── 7. SAVE ──
    const safeName = (deployment.deployment_location_name || "")
      .replace(/[^a-z0-9]+/gi, "_").slice(0, 40);
    doc.save(`Deployment_${deployment.deployment_id || ""}_${safeName}.pdf`);
  };

  // ── Now safe to reference renderPdf ──
  const img = new Image();
  img.src = "/company-logo.png";
  img.onerror = () => renderPdf(doc, pageW, marginX, dmNo, null);
  img.onload  = () => renderPdf(doc, pageW, marginX, dmNo, img);
};


const renderItemsTable = (title, list, actionType) => {
  const pendingCount = list.filter(it => it.status === 'PENDING').length;

  return (
    <div className="deployment-section">
      <div className="deployment-section-header">
        <h4>{title}</h4>
        <span>
          {list.length} item{list.length === 1 ? '' : 's'}
        </span>
        {pendingCount > 0 && deployment?.status === 'IN_PROGRESS' && (
          <button
            type="button"
            className={`deployment-btn-verify-all${awaitingApproval ? ' is-locked' : ''}`}
            onClick={() => handleVerifyAll(actionType)}
            disabled={verifyingAll[actionType] || awaitingApproval}
            title={awaitingApproval ? 'Locked until Samsung Finance approves this deployment' : undefined}
          >
            {verifyingAll[actionType]
              ? 'Verifying…'
              : awaitingApproval
              ? `Awaiting approval (${pendingCount} pending)`
              : `Verify all (${pendingCount} pending)`}
          </button>
        )}
      </div>

      {list.length === 0 ? (
        <p className="deployment-selected-empty">No items.</p>
      ) : (
        <div className="deployment-selection-table-wrapper">
          <table className="deployment-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Model</th>
                <th>Colour</th>
                <th>IMEI</th>
                <th>Serial</th>
                <th>Inv. Type</th>
                <th>Partner</th>
                <th>From Location</th>
                <th>To Location</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {list.map((it, idx) => (
                <tr key={it.deployment_item_id}>
                  <td>{idx + 1}</td>
                  <td>{it.asset1_name || '-'}</td>
                  <td>{it.asset2_name || '-'}</td>
                  <td>{it.imei_number || '-'}</td>
                  <td>{it.serial_number}</td>
                  <td>{it.inventory_type}</td>
                  <td>{it.partner_name || '-'}</td>
                  <td>{it.from_location_name || '-'}</td>
                  <td>{it.to_location_name || '-'}</td>
                  <td>{formatItemStatus(it.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

  return (
    <div className="deployment-modal-overlay">
      <div className="deployment-modal">
        <button className="deployment-close-btn" onClick={onClose}>
          ✖
        </button>

        <div className="viewdeployment-header">
  <h3 className="deployment-header-title">Deployment Details</h3>

  {canSendForApproval && (
    <button
      type="button"
      className="deployment-pdf-btn deployment-approval-btn"
      onClick={handleSendForApproval}
      disabled={sendingApproval || loading}
    >
      {sendingApproval
        ? "Sending…"
        : approvalStatus === "PENDING"
        ? "Resend Approval"
        : "Send for Approval"}
    </button>
  )}

  <button
    type="button"
    className="deployment-pdf-btn"
    onClick={() => setShowFilesModal(true)}
  >
    Upload files
  </button>

<button
    type="button"
    className="deployment-pdf-btn"
    onClick={handleGeneratePdf}
  >
    Generate PDF
  </button>
</div>

        <div className="deployment-body">
          <div className="deployment-scroll-region">
            {loading && <p>Loading deployment…</p>}
            {error && <p className="deployment-error-text">{error}</p>}

            {!loading && !error && deployment && (
              <>
                {/* ---- INFO CARD ---- */}
                <div className="view-info-card">
                  <div className="view-info-grid">
                    <div className="view-info-row">
                      <span className="view-info-label">Location: </span>
                      <span className="view-info-value">
                        {deployment.deployment_location_name || "-"}
                      </span>
                    </div>

                    <div className="view-info-row">
                      <span className="view-info-label">Deployment Type: </span>
                      <span className="view-info-value">
                        {deployment.deployment_type || "-"}
                      </span>
                    </div>

                    <div className="view-info-row">
                      <span className="view-info-label">Status: </span>
                      <span className="view-info-value">
                        {formatStatus(deployment.status)}
                      </span>
                    </div>

                    <div className="view-info-row">
                      <span className="view-info-label">Vendor: </span>
                      <span className="view-info-value">
                        {deployment.vendor_name || "-"}
                      </span>
                    </div>

                    <div className="view-info-row">
                      <span className="view-info-label">Created By: </span>
                      <span className="view-info-value">
                        {deployment.created_by_name || "-"}
                      </span>
                    </div>

                    <div className="view-info-row">
                      <span className="view-info-label">Created At: </span>
                      <span className="view-info-value">
                        {formatDateTime(deployment.created_at)}
                      </span>
                    </div>

                    <div className="view-info-row">
                      <span className="view-info-label">Completed By: </span>
                      <span className="view-info-value">
                        {deployment.completed_by_name || "-"}
                      </span>
                    </div>

                    <div className="view-info-row">
                      <span className="view-info-label">Completed At: </span>
                      <span className="view-info-value">
                        {formatDateTime(deployment.completed_at)}
                      </span>
                    </div>

                    {deployment.remarks && (
                      <div className="view-info-row wide">
                        <span className="view-info-label">Remarks: </span>
                        <span className="view-info-value">
                          {deployment.remarks}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* ---- APPROVAL (FOC devices) ---- */}
                {showApprovalPanel && (
                  <div
                    className={`deployment-approval-panel ${
                      isApproved ? "is-approved" : approvalStatus === "PENDING" ? "is-pending" : "is-required"
                    }`}
                  >
                    <div className="deployment-approval-head">
                      <strong>Samsung Finance Approval</strong>
                      <span className="deployment-approval-badge">
                        {isApproved
                          ? "Approved"
                          : approvalStatus === "PENDING"
                          ? "Awaiting approval"
                          : "Not sent"}
                      </span>
                    </div>

                    {isApproved ? (
                      <p className="deployment-approval-text">
                        Approved by{" "}
                        <strong>
                          {deployment.approved_by_name?.trim() || deployment.approved_by_email || "-"}
                        </strong>{" "}
                        on {formatDateTime(deployment.approved_at)} · PUMI No.{" "}
                        <span className="deployment-approval-pumi">{deployment.pumi_number || "-"}</span>
                      </p>
                    ) : approvalStatus === "PENDING" ? (
                      <p className="deployment-approval-text">
                        Sent {formatDateTime(deployment.approval_requested_at)}. Any Samsung Finance
                        user can approve from the email. Completion is locked until then — this
                        page updates automatically.
                      </p>
                    ) : (
                      <p className="deployment-approval-text">
                        This deployment has {focItems.length} FOC device
                        {focItems.length === 1 ? "" : "s"} and can't be completed until Samsung
                        Finance approves it. Use <strong>Send for Approval</strong> to email them
                        the approval form.
                      </p>
                    )}
                  </div>
                )}

                {/* Attachments Section */}
                <div className="deployment-attachments-block">
                  {fileError && (
                    <p className="deployment-error-text">{fileError}</p>
                  )}

                  {fileLoading && <p>Loading attachments…</p>}

                  {/* ACK PDF (download only) */}
                  <div className="deployment-attachments-row">
                    <strong>Endorsed Movement Acknowledgement (PDF):</strong>
                    {files.ackPdf ? (
                      <span className="attachment-item">
                        <a
                          href={files.ackPdf.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="deployment-file-link"
                        >
                          View / Download ({files.ackPdf.original_name})
                        </a>
                        <button
                          type="button"
                          className="attachment-delete-btn"
                          onClick={() => handleDeleteFile(files.ackPdf, 'the acknowledgement PDF')}
                          disabled={deletingFileId !== null}
                        >
                          {deletingFileId === files.ackPdf.file_id ? 'Deleting…' : 'Delete'}
                        </button>
                      </span>
                    ) : (
                      <span>No file uploaded.</span>
                    )}
                  </div>

                  {/* Photos (download only, no inline preview) */}
                  <div className="deployment-attachments-row">
                    <strong>Deployment Photos:</strong>
                    {files.photos.length === 0 ? (
                      <span>No photos uploaded.</span>
                    ) : (
                      <ul className="deployment-photo-list">
                        {files.photos.map((p, idx) => (
                          <li key={p.file_id} className="attachment-item">
                            <a
                              href={p.download_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="deployment-file-link"
                            >
                              Download Photo {idx + 1} ({p.original_name})
                            </a>
                            <button
                              type="button"
                              className="attachment-delete-btn"
                              onClick={() => handleDeleteFile(p, `photo ${idx + 1}`)}
                              disabled={deletingFileId !== null}
                            >
                              {deletingFileId === p.file_id ? 'Deleting…' : 'Delete'}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>


                {/* ---- TABLES ---- */}
                {renderItemsTable("Devices Deployed", deployItems, "DEPLOY")}
                {renderItemsTable("FOC Devices Collected", collectItems, "COLLECT")}
                {renderItemsTable("PO Devices Transferred", transferItems, "TRANSFER")}
              </>
            )}
          </div>

          <div className="deployment-footer">
            <button
              type="button"
              className="deployment-btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>

          {showFilesModal && (
            <DeploymentFilesModal
              deploymentId={deploymentId}
              existingPhotoCount={files.photos.length}
              hasAckPdf={!!files.ackPdf}
              onClose={() => setShowFilesModal(false)}
              onFilesUpdated={applyFiles}
            />
          )}

        </div>
      </div>
    </div>
  );
}